import { useState } from "react";
import { useAppContext } from "@/App";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import api from "@/lib/api";
import {
  MapPin, ChevronDown, Fuel, CheckCircle2, Navigation, Plus, X, Upload,
  Download, Wrench, Settings, Truck, ArrowRightLeft, Send, PlusCircle,
  Loader2, ThumbsUp, ThumbsDown
} from "lucide-react";
import { getMachineHighlight, getSiteHighlight, highlightBorder, highlightIcon, highlightMachineBadge } from "@/lib/highlights";

export default function SiteCard({ site, index, onSubjectDetail, showToast }) {
  const { session, hubData, refreshData, auth } = useAppContext();
  const ac = useAssetConfig(session);
  const { sites, machines, requests, events, drivers, feedback } = hubData;

  const [expandedSite, setExpandedSite] = useState(null);
  const [showAddMachine, setShowAddMachine] = useState(false);
  const [newMachine, setNewMachine] = useState({ machine_number: "", name: "", brand: "" });
  const [directReqSiteId, setDirectReqSiteId] = useState(false);
  const [directReqMachine, setDirectReqMachine] = useState("");
  const [directReqType, setDirectReqType] = useState("");
  const [directReqNotes, setDirectReqNotes] = useState("");
  const [directReqSubmitting, setDirectReqSubmitting] = useState(false);
  const [transportApproveId, setTransportApproveId] = useState(null);
  const [transportDriver, setTransportDriver] = useState("");

  const sReqs = requests.filter(r => r.site_id === site.id);
  const sMachines = machines.filter(m => m.site_id === site.id);
  const hasUrgent = sReqs.some(r => r.urgency === "URGENT" && r.status !== "DONE");
  const newReqs = sReqs.filter(r => r.status === "NEW" || (r.status === "IN_PROGRESS" && r.request_type === "TRANSPORT"));
  const isExpanded = expandedSite === site.id;
  const siteHL = getSiteHighlight(site, machines, requests);

  const handleApprove = async (reqId, driverName) => {
    await api.approveRequest(reqId, driverName);
    showToast("Approved!");
    refreshData();
  };

  const handleConfirmTransport = async (reqId) => {
    await api.confirmTransport(reqId);
    showToast("Transport confirmed!");
    refreshData();
  };

  const handleAddMachine = async () => {
    if (!newMachine.machine_number.trim()) return;
    await api.createMachine({ hub_id: session.hubId, site_id: site.id, ...newMachine });
    showToast("Machine added!");
    setNewMachine({ machine_number: "", name: "", brand: "" });
    setShowAddMachine(false);
    refreshData();
  };

  const handleDeleteMachine = async (machineId) => {
    await api.deleteMachine(machineId);
    showToast("Machine removed");
    refreshData();
  };

  const handleDeleteSite = async () => {
    await api.deleteSite(site.id);
    showToast("Site removed");
    refreshData();
  };

  const handleDirectRequest = async () => {
    if (!directReqMachine) return;
    setDirectReqSubmitting(true);
    const machine = machines.find(m => m.id === directReqMachine);
    const reqType = directReqType || ac.requestType;
    try {
      await api.createRequest({
        hub_id: session.hubId, machine_id: machine?.id || directReqMachine,
        machine_number: machine?.machine_number || directReqMachine,
        site_id: site.id, site_name: site.name, request_type: reqType,
        urgency: "NONE", products: [],
        notes: directReqNotes || `${reqType} requested by ${session?.userName || "Planner"}`,
        requester_type: "PLANNER", current_fuel_level: 0,
        gps_lat: site.gps_lat || 0, gps_lon: site.gps_lon || 0,
      });
      showToast(`${ac.requestLabel} created!`);
      setDirectReqSiteId(false);
      setDirectReqMachine(""); setDirectReqType(""); setDirectReqNotes("");
      refreshData();
    } catch { showToast("Failed to create request"); }
    setDirectReqSubmitting(false);
  };

  const sfb = (feedback || []).filter(fb => fb.site_id === site.id).slice(0, 5);

  return (
    <div data-testid={`site-row-${site.id}`}
      className={`bg-slate-50 border rounded-xl overflow-hidden transition-all animate-fadeInUp ${hasUrgent ? "border-red-600/40" : siteHL ? highlightBorder(siteHL) : "border-slate-200 hover:border-slate-300"}`}
      style={{ animationDelay: `${index * 0.05}s` }}>
      <div role="button" tabIndex={0} onClick={() => setExpandedSite(isExpanded ? null : site.id)}
        className="w-full p-4 flex items-center justify-between text-left cursor-pointer">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${hasUrgent ? "bg-red-50 text-red-500" : highlightIcon(siteHL)}`}>
            <MapPin className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight">{site.name}</h3>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{site.city} / {site.region}</span>
              {site.current_stage && <span className="text-[9px] font-bold bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">{site.current_stage}</span>}
              {site.gps_lat > 0 && <span className="text-[9px] font-mono text-lime-500/60 flex items-center gap-0.5"><Navigation className="w-2.5 h-2.5" /> GPS</span>}
              {site.main_driver && <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded">{site.main_driver}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {siteHL === "red" && <span className="text-[9px] font-bold bg-red-500/10 text-red-400 px-2 py-1 rounded flex items-center gap-1 animate-pulse"><Fuel className="w-3 h-3" /> {ac.requestType}</span>}
          {siteHL === "green" && <span className="text-[9px] font-bold bg-lime-50 text-lime-600 px-2 py-1 rounded flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SUPPLIED</span>}
          {newReqs.length > 0 && <span className="px-2 py-1 bg-lime-50 text-lime-600 rounded text-[10px] font-bold">{newReqs.length} NEW</span>}
          {hasUrgent && <span className="px-2 py-1 bg-red-50 text-red-500 rounded text-[10px] font-bold animate-pulse">URGENT</span>}
          <span className="text-[10px] font-mono text-slate-400">{sMachines.length} machines</span>
          <button onClick={e => { e.stopPropagation(); handleDeleteSite(); }} className="p-1 text-slate-400 hover:text-red-400 transition-all" data-testid={`delete-site-${site.id}`}>
            <X className="w-3.5 h-3.5" />
          </button>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-200 p-4 space-y-3 animate-fadeIn">
          {/* GPS */}
          {site.gps_lat > 0 && (
            <a href={`https://www.google.com/maps?q=${site.gps_lat},${site.gps_lon}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl hover:border-lime-300 transition-all" data-testid={`site-gps-${site.id}`}>
              <Navigation className="w-3.5 h-3.5 text-lime-500" />
              <span className="text-[10px] font-mono text-lime-600">{site.gps_lat.toFixed(5)}, {site.gps_lon.toFixed(5)}</span>
              <span className="text-[9px] text-slate-400 ml-auto">Maps</span>
            </a>
          )}
          {(site.lock_codes || site.accessibility_route) && (
            <div className="flex gap-2">
              {site.lock_codes && <span className="text-[10px] font-bold bg-amber-50 text-amber-400 px-2 py-1 rounded flex items-center gap-1"><span className="text-slate-400">Code:</span> {site.lock_codes}</span>}
              {site.accessibility_route && <span className="text-[10px] font-bold bg-blue-500/10 text-blue-400 px-2 py-1 rounded flex items-center gap-1 truncate max-w-[200px]"><span className="text-slate-400">Route:</span> {site.accessibility_route}</span>}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-2.5 bg-white rounded-xl">
              <span className="text-[9px] font-bold text-slate-400 uppercase block">New</span>
              <span className="text-lg font-black text-lime-600 font-mono">{sReqs.filter(r => r.status === "NEW").length}</span>
            </div>
            <div className="p-2.5 bg-white rounded-xl">
              <span className="text-[9px] font-bold text-slate-400 uppercase block">Active</span>
              <span className="text-lg font-black text-amber-400 font-mono">{sReqs.filter(r => r.status === "IN_PROGRESS").length}</span>
            </div>
            <div className="p-2.5 bg-white rounded-xl">
              <span className="text-[9px] font-bold text-slate-400 uppercase block">Done</span>
              <span className="text-lg font-black text-slate-900 font-mono">{sReqs.filter(r => r.status === "DONE").length}</span>
            </div>
            <div className="p-2.5 bg-white rounded-xl">
              <span className="text-[9px] font-bold text-slate-400 uppercase block">Machines</span>
              <span className="text-lg font-black text-slate-900 font-mono">{sMachines.length}</span>
            </div>
          </div>

          {/* Machine badges */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Machines on site ({sMachines.length})</span>
              <div className="flex items-center gap-2">
                <button onClick={() => onSubjectDetail({ type: "site", subject: site })} className="text-[9px] font-bold text-slate-400 hover:text-amber-400 flex items-center gap-1" data-testid={`site-log-${site.id}`}>
                  <Download className="w-3 h-3" /> View Log
                </button>
                <button onClick={() => setShowAddMachine(!showAddMachine)}
                  className="text-[9px] font-bold text-lime-500 hover:text-lime-600 flex items-center gap-1" data-testid={`add-machine-btn-${site.id}`}>
                  <Plus className="w-3 h-3" /> Set Machines
                </button>
              </div>
            </div>
            {sMachines.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sMachines.map(m => {
                  const mHL = getMachineHighlight(m, requests);
                  return (
                    <span key={m.id} onClick={() => onSubjectDetail({ type: "machine", subject: m })} className={`text-[9px] font-bold px-2 py-1 rounded flex items-center gap-1 border cursor-pointer hover:shadow-sm transition-all ${
                      mHL ? highlightMachineBadge(mHL) :
                      m.status_type === "DEFECT" ? "border-red-500/30 bg-red-500/10 text-red-400" :
                      m.status_type === "TRANSPORT" ? "border-blue-500/30 bg-blue-500/10 text-blue-400" :
                      m.status_type === "UPKEEP" ? "border-purple-500/30 bg-purple-500/10 text-purple-400" :
                      "border-slate-200 bg-white text-slate-400"
                    }`} data-testid={`machine-badge-${m.id}`}>
                      {mHL === "red" && <Fuel className="w-2.5 h-2.5" />}
                      {mHL === "green" && <CheckCircle2 className="w-2.5 h-2.5" />}
                      {!mHL && m.status_type === "DEFECT" && <Wrench className="w-2.5 h-2.5" />}
                      {!mHL && m.status_type === "TRANSPORT" && <Truck className="w-2.5 h-2.5" />}
                      {!mHL && m.status_type === "UPKEEP" && <Settings className="w-2.5 h-2.5" />}
                      {m.machine_number}
                      {m.gps_lat > 0 && <Navigation className="w-2 h-2 text-lime-500" />}
                      <button onClick={e => { e.stopPropagation(); onSubjectDetail({ type: "machine", subject: m }); }} className="ml-0.5 text-slate-400 hover:text-amber-400" data-testid={`machine-log-${m.id}`}>
                        <Download className="w-2.5 h-2.5" />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDeleteMachine(m.id); }} className="text-slate-400 hover:text-red-400" data-testid={`delete-machine-${m.id}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {/* Add Machine Panel */}
            {showAddMachine && (
              <div className="mt-2 p-3 bg-white border border-lime-500/20 rounded-xl space-y-2 animate-fadeIn" data-testid={`set-machines-panel-${site.id}`}>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Add machines via Philogic AI</p>
                <div className="flex gap-2">
                  <input value={newMachine.machine_number} onChange={e => setNewMachine(p => ({ ...p, machine_number: e.target.value }))} placeholder="Number *"
                    className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[10px] font-bold text-slate-400 w-20" />
                  <input value={newMachine.name} onChange={e => setNewMachine(p => ({ ...p, name: e.target.value }))} placeholder="Name"
                    className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[10px] font-bold text-slate-400 flex-1" />
                  <input value={newMachine.brand} onChange={e => setNewMachine(p => ({ ...p, brand: e.target.value }))} placeholder="Brand"
                    className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[10px] font-bold text-slate-400 w-24" />
                  <button onClick={handleAddMachine} disabled={!newMachine.machine_number.trim()}
                    className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-[10px] font-bold disabled:bg-slate-700" data-testid={`confirm-add-machine-${site.id}`}>Add</button>
                </div>
                <textarea placeholder="Bulk paste machine list here... Philogic AI will decipher"
                  className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-[10px] font-bold text-slate-400 h-16 resize-none" data-testid={`bulk-paste-${site.id}`}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && e.ctrlKey && e.target.value.trim()) {
                      const text = e.target.value.trim();
                      e.target.value = "";
                      showToast("Philogic analyzing...");
                      try {
                        const res = await api.importMachines(auth?.jwt, session.hubId, site.id, { pasteText: text });
                        showToast(`${res.imported} machines imported!`);
                        refreshData();
                      } catch { showToast("Import failed"); }
                    }
                  }}
                />
                <div className="flex gap-2 items-center">
                  <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-dashed border-slate-200 rounded cursor-pointer hover:border-lime-400 text-[10px] font-bold text-slate-400 flex-1" data-testid={`bulk-upload-${site.id}`}>
                    <Upload className="w-3 h-3" /> Upload PDF/CSV/Photo
                    <input type="file" accept=".pdf,.csv,.jpg,.jpeg,.png,.txt" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      showToast("Philogic analyzing...");
                      try {
                        const res = await api.importMachines(auth?.jwt, session.hubId, site.id, { file });
                        showToast(`${res.imported} machines imported!`);
                        refreshData();
                      } catch { showToast("Import failed"); }
                      e.target.value = "";
                    }} />
                  </label>
                  <span className="text-[8px] text-slate-400">Ctrl+Enter to submit paste</span>
                </div>
              </div>
            )}
          </div>

          {/* Direct Request (no machinist mode) */}
          {!ac.hasMachinist && (
            <div className="pt-2" data-testid={`direct-request-section-${site.id}`}>
              {directReqSiteId ? (
                <div className="p-3 bg-lime-50 border border-lime-300 rounded-xl space-y-2 animate-fadeIn" data-testid={`direct-request-form-${site.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-lime-700 uppercase tracking-widest flex items-center gap-1"><Send className="w-3 h-3" /> New {ac.requestLabel}</span>
                    <button onClick={() => setDirectReqSiteId(false)} className="text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  <select value={directReqMachine} onChange={e => setDirectReqMachine(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-900" data-testid={`direct-req-machine-${site.id}`}>
                    <option value="">Select machine...</option>
                    {sMachines.map(m => <option key={m.id} value={m.id}>{m.machine_number} {m.name ? `— ${m.name}` : ""}</option>)}
                  </select>
                  {ac.isTaskBased && ac.actions?.length > 0 && (
                    <select value={directReqType} onChange={e => setDirectReqType(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-bold text-slate-900" data-testid={`direct-req-action-${site.id}`}>
                      <option value="">Select action...</option>
                      {ac.actions.map((a, i) => <option key={i} value={a}>{a}</option>)}
                    </select>
                  )}
                  <input value={directReqNotes} onChange={e => setDirectReqNotes(e.target.value)} placeholder="Notes (optional)"
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-slate-900 placeholder:text-slate-400" data-testid={`direct-req-notes-${site.id}`} />
                  <button onClick={handleDirectRequest} disabled={!directReqMachine || directReqSubmitting}
                    className="w-full py-2.5 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-200 disabled:text-slate-400 text-slate-950 rounded-lg text-xs font-black uppercase flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all"
                    data-testid={`direct-req-submit-${site.id}`}>
                    {directReqSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5" /> Create {ac.requestLabel}</>}
                  </button>
                </div>
              ) : (
                <button onClick={() => { setDirectReqSiteId(true); setDirectReqType(ac.requestType); }}
                  className="flex items-center gap-1.5 text-[10px] font-bold text-lime-600 hover:text-lime-500 py-1" data-testid={`direct-req-btn-${site.id}`}>
                  <PlusCircle className="w-3.5 h-3.5" /> Create {ac.requestLabel}
                </button>
              )}
            </div>
          )}

          {/* Requests */}
          {newReqs.map(req => {
            const reqType = req.request_type || ac.requestType;
            const isSupply = reqType === ac.requestType || reqType === "FUEL" || reqType === "SUPPLY";
            return (
              <div key={req.id} className={`p-3 bg-white border rounded-xl flex items-center justify-between ${
                reqType === "TRANSPORT" ? "border-blue-500/30" : reqType === "REPARATION" ? "border-red-500/30" : reqType === "MAINTENANCE" ? "border-purple-500/30" : "border-slate-200"
              }`} data-testid={`request-${req.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                      isSupply ? "bg-lime-50 text-lime-600" : reqType === "REPARATION" ? "bg-red-500/10 text-red-400" : reqType === "MAINTENANCE" ? "bg-purple-500/10 text-purple-400" : "bg-blue-500/10 text-blue-400"
                    }`}>
                      {isSupply && <Fuel className="w-2.5 h-2.5" />}
                      {reqType === "REPARATION" && <Wrench className="w-2.5 h-2.5" />}
                      {reqType === "MAINTENANCE" && <Settings className="w-2.5 h-2.5" />}
                      {reqType === "TRANSPORT" && <ArrowRightLeft className="w-2.5 h-2.5" />}
                      {isSupply ? ac.requestType : reqType}
                    </span>
                    <span className="text-xs font-bold text-slate-800">{req.machine_number}</span>
                  </div>
                  <span className="text-[10px] text-slate-400">{req.is_fun_request ? `Fun: ${req.fun_message}` : req.notes}</span>
                  {reqType === "TRANSPORT" && req.target_site_name && (
                    <p className="text-[10px] text-blue-400 font-bold mt-1 flex items-center gap-1"><ArrowRightLeft className="w-3 h-3" /> To: {req.target_site_name}</p>
                  )}
                  {req.gps_lat > 0 && (
                    <p className="text-[9px] text-slate-400 font-mono mt-0.5 flex items-center gap-1"><Navigation className="w-2.5 h-2.5" /> {req.gps_lat.toFixed(4)}, {req.gps_lon.toFixed(4)}</p>
                  )}
                </div>
                <div className="flex gap-1.5 ml-2">
                  {reqType === "TRANSPORT" ? (
                    req.status === "IN_PROGRESS" && req.assigned_driver ? (
                      <button data-testid={`confirm-transport-${req.id}`} onClick={() => handleConfirmTransport(req.id)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold uppercase active:scale-95 transition-all flex items-center gap-1">
                        <Truck className="w-3 h-3" /> Confirm Arrival
                      </button>
                    ) : transportApproveId === req.id ? (
                      <div className="flex items-center gap-1.5" data-testid={`transport-assign-${req.id}`}>
                        <select value={transportDriver} onChange={e => setTransportDriver(e.target.value)}
                          className="bg-white border border-slate-200 rounded px-2 py-1 text-[10px] font-bold text-slate-900 w-28" data-testid={`transport-driver-select-${req.id}`}>
                          <option value="">Driver...</option>
                          {drivers.map(d => <option key={d.driver_name} value={d.driver_name}>{d.driver_name}</option>)}
                        </select>
                        <button disabled={!transportDriver} onClick={async () => { await handleApprove(req.id, transportDriver); setTransportApproveId(null); setTransportDriver(""); }}
                          className="px-2 py-1 bg-blue-600 disabled:bg-slate-200 text-white disabled:text-slate-400 rounded text-[10px] font-bold" data-testid={`transport-dispatch-${req.id}`}>Dispatch</button>
                        <button onClick={() => setTransportApproveId(null)} className="p-1 text-slate-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <button data-testid={`approve-transport-${req.id}`} onClick={() => { setTransportApproveId(req.id); setTransportDriver(site.main_driver || ""); }}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold uppercase active:scale-95 transition-all flex items-center gap-1">
                        <ArrowRightLeft className="w-3 h-3" /> Assign Driver
                      </button>
                    )
                  ) : (
                    <button data-testid={`approve-${req.id}`} onClick={() => handleApprove(req.id, site.main_driver)}
                      className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-xs font-bold uppercase active:scale-95 transition-all">
                      Approve
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Feedback Log */}
          {sfb.length > 0 && (
            <div className="pt-2 space-y-1.5" data-testid={`planner-feedback-log-${site.id}`}>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Driver Feedback Log</span>
              {sfb.map(fb => (
                <div key={fb.id} className={`p-2.5 rounded border text-[11px] font-bold ${
                  fb.feedback_type === "NEGATIVE" ? "border-red-500/30 bg-red-500/5 text-red-400" : "border-lime-300 bg-lime-500/5 text-lime-600"
                }`} data-testid={`planner-fb-${fb.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      {fb.feedback_type === "NEGATIVE" ? <ThumbsDown className="w-3 h-3" /> : <ThumbsUp className="w-3 h-3" />}
                      {fb.author}
                    </span>
                    <span className="text-[9px] text-slate-400">{new Date(fb.date).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                  </div>
                  <p className={`mt-0.5 ${fb.feedback_type === "NEGATIVE" ? "text-red-300" : "text-lime-300"}`}>{fb.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
